/**
 * Pure placement arithmetic for comment pins. Every decision the pin layer,
 * the composer and the rail make about *where* a pin sits lives here, over
 * plain data, so it can be tested without a Liveblocks room or a React Flow
 * store.
 */

import {
  boundsFromRect,
  isExcludedNodeType,
  rectsIntersect,
} from "../utils/sectionMembership";

/** The subset of ThreadMetadata that describes a pin's placement. */
export type ThreadPlacement = {
  x: number;
  y: number;
  anchorNodeId?: string;
  offsetX?: number;
  offsetY?: number;
  orphaned?: boolean;
};

/** A board node reduced to what placement needs. `x`/`y` are parent-relative. */
export type NodeBounds = {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  type?: string;
  parentId?: string;
};

/**
 * Absolute flow position of a node, summing every ancestor offset. Stops when
 * a parent is missing from the map, mirroring the `canvas:focus-node` walk in
 * useCanvasWindowEvents. Returns null when the node itself is unknown.
 */
export function absoluteNodePosition(
  id: string,
  nodesById: Map<string, NodeBounds>,
): { x: number; y: number } | null {
  const node = nodesById.get(id);
  if (!node) return null;

  let x = node.x;
  let y = node.y;
  const seen = new Set<string>([node.id]);
  let parentId = node.parentId;
  while (parentId && !seen.has(parentId)) {
    const parent = nodesById.get(parentId);
    if (!parent) break;
    x += parent.x;
    y += parent.y;
    seen.add(parent.id);
    parentId = parent.parentId;
  }
  return { x, y };
}

/**
 * Absolute world position of a pin. An anchor that resolves wins — the pin
 * follows its node through moves, section reparents and zoom without a single
 * metadata write. An anchor that does not resolve falls back to the frozen
 * coordinate the orphan sweep left behind.
 */
export function resolvePinPosition(
  metadata: ThreadPlacement,
  nodesById: Map<string, NodeBounds>,
): { x: number; y: number } {
  if (!metadata.anchorNodeId) return { x: metadata.x, y: metadata.y };

  const anchor = absoluteNodePosition(metadata.anchorNodeId, nodesById);
  if (!anchor) return { x: metadata.x, y: metadata.y };

  return {
    x: anchor.x + (metadata.offsetX ?? 0),
    y: anchor.y + (metadata.offsetY ?? 0),
  };
}

/**
 * Flow-space inset of a node-anchored pin's center from the node's corner. A
 * pin straddles its point, so an offset of exactly the corner leaves half the
 * bubble off the node; this pulls it inside.
 */
export const NODE_PIN_CORNER_INSET = 24;

/**
 * Where a comment opened from a node's own menu lands: anchored to the node,
 * so it travels with it, at `point` when the menu carried the pointer that
 * opened it and at the node's top-right corner otherwise. The corner is inset
 * by `NODE_PIN_CORNER_INSET`, clamped to half the node so the pin still sits
 * on a node smaller than the inset.
 */
export function placementForNodeComment(
  node: NodeBounds,
  absolute: { x: number; y: number },
  point?: { x: number; y: number } | null,
): ThreadPlacement {
  const offsetX = point
    ? point.x - absolute.x
    : node.width - Math.min(NODE_PIN_CORNER_INSET, node.width / 2);
  const offsetY = point
    ? point.y - absolute.y
    : Math.min(NODE_PIN_CORNER_INSET, node.height / 2);

  return {
    x: absolute.x + offsetX,
    y: absolute.y + offsetY,
    anchorNodeId: node.id,
    offsetX,
    offsetY,
  };
}

/**
 * A metadata patch. Liveblocks clears an optional metadata field with `null`,
 * so detaching writes nulls rather than dropping the keys.
 */
export type ThreadPlacementPatch = {
  x?: number;
  y?: number;
  anchorNodeId?: string | null;
  offsetX?: number | null;
  offsetY?: number | null;
  orphaned?: boolean | null;
};

function area(node: NodeBounds): number {
  return node.width * node.height;
}

/**
 * Where a pin dropped at `point` lands. The smallest non-excluded candidate
 * the point falls inside wins, mirroring `pickTargetSection`; nothing under
 * the point means free-floating.
 *
 * `nodesById` resolves ancestors, so `candidates` can be a filtered subset.
 */
export function placementForDrop(
  point: { x: number; y: number },
  candidates: NodeBounds[],
  nodesById: Map<string, NodeBounds> = new Map(
    candidates.map((c) => [c.id, c]),
  ),
): ThreadPlacement {
  const hit = boundsFromRect(point, 0, 0);

  let best: { node: NodeBounds; absolute: { x: number; y: number } } | null =
    null;
  for (const candidate of candidates) {
    if (isExcludedNodeType(candidate.type)) continue;
    const absolute = absoluteNodePosition(candidate.id, nodesById);
    if (!absolute) continue;
    if (
      !rectsIntersect(
        hit,
        boundsFromRect(absolute, candidate.width, candidate.height),
      )
    )
      continue;
    if (best && area(best.node) <= area(candidate)) continue;
    best = { node: candidate, absolute };
  }

  if (!best) return { x: point.x, y: point.y };

  return {
    x: point.x,
    y: point.y,
    anchorNodeId: best.node.id,
    offsetX: point.x - best.absolute.x,
    offsetY: point.y - best.absolute.y,
  };
}

/**
 * Stick or unstick, decided once at drag stop. An anchored result carries no
 * `x`/`y`: the frozen coordinate only matters while the pin is detached, and
 * rewriting it on every drag would churn metadata the render path ignores.
 */
export function placementPatchForDragStop(
  prev: ThreadPlacement,
  point: { x: number; y: number },
  candidates: NodeBounds[],
  nodesById: Map<string, NodeBounds>,
): ThreadPlacementPatch {
  const next = placementForDrop(point, candidates, nodesById);
  const clearOrphan = prev.orphaned ? { orphaned: null } : {};

  if (next.anchorNodeId) {
    return {
      anchorNodeId: next.anchorNodeId,
      offsetX: next.offsetX,
      offsetY: next.offsetY,
      ...clearOrphan,
    };
  }

  return {
    x: next.x,
    y: next.y,
    anchorNodeId: null,
    offsetX: null,
    offsetY: null,
    ...clearOrphan,
  };
}

/**
 * Deleting a node detaches its threads; it never deletes them. Each affected
 * pin freezes at the anchor's last absolute position — call this before the
 * nodes leave the store, so that position is still resolvable.
 */
export function orphanPatchesForDeletedNodes(
  threads: { id: string; metadata: ThreadPlacement }[],
  deletedIds: Set<string>,
  nodesById: Map<string, NodeBounds>,
): { threadId: string; patch: ThreadPlacementPatch }[] {
  const patches: { threadId: string; patch: ThreadPlacementPatch }[] = [];

  for (const thread of threads) {
    const anchorNodeId = thread.metadata.anchorNodeId;
    if (!anchorNodeId || !deletedIds.has(anchorNodeId)) continue;

    const frozen = resolvePinPosition(thread.metadata, nodesById);
    patches.push({
      threadId: thread.id,
      patch: {
        x: frozen.x,
        y: frozen.y,
        anchorNodeId: null,
        offsetX: null,
        offsetY: null,
        orphaned: true,
      },
    });
  }

  return patches;
}
